import { useState, useRef } from 'react';
import { useApp } from '@/contexts/AppContext';
import { useAuth } from '@/contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Trash, FileText, Plus, Variable, Copy, ChevronDown, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { processTemplateVariables, TEMPLATE_VARIABLES, insertVariableAtCursor } from '@/lib/templateVariables';
import { copyToClipboard } from '@/lib/clipboard';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

export default function TemplateDialog({ onClose }) {
  const { templates, createTemplate, updateTemplate, deleteTemplate, createNote } = useApp();
  const { user } = useAuth();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [content, setContent] = useState('');
  const [showVariables, setShowVariables] = useState(false);
  const contentRef = useRef(null);

  const resetForm = () => {
    setShowForm(false);
    setEditingId(null);
    setName('');
    setDescription('');
    setContent('');
  };

  const handleStartEdit = (template) => {
    setEditingId(template.id);
    setName(template.name);
    setDescription(template.description || '');
    setContent(template.content || '');
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!name.trim()) return;
    if (editingId) {
      await updateTemplate(editingId, { name: name.trim(), description, content });
      toast.success('Template updated');
    } else {
      await createTemplate({ name: name.trim(), description, content });
      toast.success('Template created');
    }
    resetForm();
  };

  const handleUseTemplate = async (template) => {
    // Process template variables before creating note
    const processedContent = processTemplateVariables(template.content, {
      title: template.name,
      user: user?.email || '',
    });
    
    await createNote({ title: template.name, content: processedContent });
    toast.success(`Note created from template "${template.name}"`);
    onClose();
  };

  const handleInsertVariable = (variable) => {
    if (contentRef.current) {
      insertVariableAtCursor(contentRef.current, variable, setContent);
    }
    setShowVariables(false);
  };

  const copyVariable = async (variable) => {
    const result = await copyToClipboard(variable);
    if (result.success) {
      toast.success('Variable copied to clipboard');
    } else {
      toast.info('Press Ctrl+C (or Cmd+C on Mac) to copy');
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg" data-testid="template-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Templates
          </DialogTitle>
          <DialogDescription>
            Create reusable note templates with dynamic variables.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {showForm ? (
            <div className="space-y-3 p-3 border border-border rounded-md bg-secondary/20">
              <p className="text-xs font-medium text-muted-foreground">
                {editingId ? 'Edit Template' : 'New Template'}
              </p>
              <Input 
                value={name} 
                onChange={(e) => setName(e.target.value)} 
                placeholder="Template name" 
                className="h-9 text-sm" 
                data-testid="template-name-input" 
              />
              <Input 
                value={description} 
                onChange={(e) => setDescription(e.target.value)} 
                placeholder="Description (optional)" 
                className="h-9 text-sm" 
                data-testid="template-desc-input" 
              />
              
              {/* Variable Insert Button */}
              <div className="flex items-center justify-between">
                <Popover open={showVariables} onOpenChange={setShowVariables}>
                  <PopoverTrigger asChild>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="h-7 text-xs gap-1"
                      data-testid="template-insert-variable-btn"
                    >
                      <Variable className="w-3.5 h-3.5" />
                      Insert Variable
                      <ChevronDown className="w-3 h-3" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80 p-2" align="start">
                    <div className="space-y-1">
                      <p className="text-xs font-medium text-muted-foreground px-2 pb-1">
                        Available Variables
                      </p>
                      {TEMPLATE_VARIABLES.map((v) => (
                        <div 
                          key={v.variable}
                          className="flex items-center justify-between p-2 rounded-md hover:bg-muted/50 group cursor-pointer"
                          onClick={() => handleInsertVariable(v.variable)}
                          data-testid={`template-var-${v.variable.replace(/[{}]/g, '')}`}
                        >
                          <div className="flex-1 min-w-0">
                            <code className="text-xs font-mono text-accent-amber">{v.variable}</code>
                            <p className="text-[10px] text-muted-foreground truncate">{v.description}</p>
                          </div>
                          <button
                            onClick={(e) => { e.stopPropagation(); copyVariable(v.variable); }}
                            className="p-1 text-muted-foreground hover:text-foreground opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Copy variable"
                          >
                            <Copy className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
                <span className="text-[10px] text-muted-foreground">
                  Variables are replaced when you use the template
                </span>
              </div>

              <textarea
                ref={contentRef}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Template content (Markdown)...&#10;&#10;Try using variables like {{date}} or {{random}}"
                className="w-full h-40 bg-secondary/50 border border-border rounded-md p-3 text-sm font-mono resize-none outline-none focus:ring-1 focus:ring-ring"
                data-testid="template-content-input"
              />
              
              {/* Preview Section */}
              {content && (
                <div className="p-3 bg-muted/30 border border-border rounded-md" data-testid="template-preview">
                  <p className="text-[10px] font-medium text-muted-foreground mb-1.5">
                    Preview{content.includes('{{') ? ' (variables processed)' : ''}
                  </p>
                  <div className="text-sm prose prose-sm dark:prose-invert max-w-none max-h-40 overflow-y-auto
                    prose-headings:my-1 prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0.5
                    prose-code:text-accent-amber prose-code:bg-muted/50 prose-code:px-1 prose-code:rounded">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {processTemplateVariables(content, { title: name || 'Untitled', user: user?.email || 'you@example.com' })}
                    </ReactMarkdown>
                  </div>
                </div>
              )}

              <div className="flex gap-2">
                <Button onClick={handleSave} size="sm" className="h-8" data-testid="template-save-btn">
                  {editingId ? 'Update Template' : 'Save Template'}
                </Button>
                <Button onClick={resetForm} size="sm" variant="ghost" className="h-8">
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <Button onClick={() => setShowForm(true)} variant="outline" size="sm" className="w-full h-9" data-testid="template-new-btn">
              <Plus className="w-4 h-4 mr-1.5" /> Create Template
            </Button>
          )}

          <div className="space-y-1 max-h-72 overflow-y-auto">
            {templates.map((t) => (
              <div 
                key={t.id} 
                className="flex items-center gap-2 p-2.5 rounded-md hover:bg-muted/50 group border border-transparent hover:border-border transition-all" 
                data-testid={`template-item-${t.id}`}
              >
                <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{t.name}</p>
                  {t.description && <p className="text-xs text-muted-foreground truncate">{t.description}</p>}
                  {t.content && t.content.includes('{{') && (
                    <p className="text-[10px] text-accent-amber mt-0.5">
                      <Variable className="w-3 h-3 inline mr-0.5" />
                      Contains variables
                    </p>
                  )}
                </div>
                <Button 
                  onClick={() => handleUseTemplate(t)} 
                  size="sm" 
                  variant="ghost" 
                  className="h-7 text-xs opacity-0 group-hover:opacity-100 transition-opacity" 
                  data-testid={`template-use-${t.id}`}
                >
                  Use
                </Button>
                <button 
                  onClick={() => handleStartEdit(t)} 
                  className="p-1.5 text-muted-foreground hover:text-foreground opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`template-edit-${t.id}`}
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button 
                  onClick={() => deleteTemplate(t.id)} 
                  className="p-1.5 text-muted-foreground hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`template-delete-${t.id}`}
                >
                  <Trash className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
            {templates.length === 0 && !showForm && (
              <div className="text-center py-8">
                <FileText className="w-10 h-10 mx-auto text-muted-foreground/20 mb-2" />
                <p className="text-sm text-muted-foreground">No templates yet</p>
                <p className="text-xs text-muted-foreground/70 mt-1">Create one to get started</p>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
